import { useOutsideClick } from "@/hooks/useOutsideClick";
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "sonner";
import { getCookie } from "@/lib/cookies";
import { API_URL } from "@/env";
import { showCustomToast } from "../CustomToast";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
import { useNavigate } from "react-router-dom";

// Color palette for workspace avatars
const COLOR_PALETTE = [
  { bg: "bg-[#E54D2E]/[0.16]", text: "text-[#E54D2E]", value: "#E54D2E" },
  { bg: "bg-[#E93D82]/[0.16]", text: "text-[#E93D82]", value: "#E93D82" },
  { bg: "bg-[#AB4ABA]/[0.16]", text: "text-[#AB4ABA]", value: "#AB4ABA" },
  { bg: "bg-[#5958B1]/[0.16]", text: "text-[#5958B1]", value: "#5958B1" },
  { bg: "bg-[#0090FF]/[0.16]", text: "text-[#0090FF]", value: "#0090FF" },
  { bg: "bg-[#29A383]/[0.16]", text: "text-[#29A383]", value: "#29A383" },
  { bg: "bg-[#46A758]/[0.16]", text: "text-[#46A758]", value: "#46A758" },
  { bg: "bg-[#AD7F58]/[0.16]", text: "text-[#AD7F58]", value: "#AD7F58" },
  { bg: "bg-[#F76B15]/[0.16]", text: "text-[#F76B15]", value: "#F76B15" },
  { bg: "bg-[#197CAE]/[0.16]", text: "text-[#197CAE]", value: "#197CAE" },
];

const CreateWorkspaceModal = ({
  setIsOpen,
  onWorkspaceCreated,
}: {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onWorkspaceCreated?: () => void;
}) => {
  const token = getCookie("token");
  const [workspaceName, setWorkspaceName] = useState("");
  const [selected, setSelected] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [emails, setEmails] = useState("");
  const [emailError, setEmailError] = useState("");
  const [nameError, setNameError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [selectedColor, setSelectedColor] = useState(COLOR_PALETTE[0]);
  const modalRef = useRef(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();

  const { setCurrentWorkspace, addWorkspace } = useWorkspaceStore();

  // Select a random color when modal opens
  useEffect(() => {
    const randomColor = COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)];
    setSelectedColor(randomColor);
  }, []);

  useOutsideClick(modalRef, () => {
    setIsOpen(false);
  });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [setIsOpen]);

  const handleTextareaResize = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "84px"; // Reset to min height
      const newHeight = Math.max(84, Math.min(textarea.scrollHeight, 200)); // Max 200px
      textarea.style.height = `${newHeight}px`;
    }
  };

  const validateEmails = (emailString: string) => {
    if (!emailString.trim()) {
      setEmailError("");
      return true;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailList = emailString
      .split(/[\s,]+/)
      .filter((email) => email.trim());

    const invalidEmails = emailList.filter(
      (email) => !emailRegex.test(email.trim())
    );

    if (invalidEmails.length > 0) {
      setEmailError(`Invalid email(s): ${invalidEmails.join(", ")}`);
      return false;
    }

    setEmailError("");
    return true;
  };

  const checkWorkspaceName = async (name: string) => {
    if (!name.trim()) {
      setNameError("");
      return true;
    }

    try {
      setIsCheckingName(true);
      const response = await fetch(`${API_URL}/workspaces/check-name`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name }),
      });

      if (!response.ok) {
        throw new Error("Failed to check workspace name");
      }

      const result = await response.json();

      if (result.exists) {
        setNameError(`You already have a workspace named "${name}"`);
        return false;
      }

      setNameError("");
      return true;
    } catch (error) {
      console.error("Error checking workspace name:", error);
      // Don't block the user if the check fails
      return true;
    } finally {
      setIsCheckingName(false);
    }
  };

  const createWorkspace = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/workspaces`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: workspaceName,
          allowDomainJoin: selected,
          color: selectedColor.value,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create workspace");
      }

      const workspace = await response.json();
      setCurrentWorkspace(workspace);
      addWorkspace(workspace);
      navigate(`/dashboard/${workspace._id}`);
      return workspace;
    } catch (error) {
      console.error("Error creating workspace:", error);
      toast.error("Failed to create workspace");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const inviteUsers = async (workspaceId: string, emailList: string[]) => {
    try {
      const response = await fetch(
        `${API_URL}/workspaces/${workspaceId}/invite`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            emails: emailList,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to invite users");
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error inviting users:", error);
      toast.error("Failed to invite users");
      throw error;
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      const isValid = await checkWorkspaceName(workspaceName);
      if (isValid) {
        setCurrentStep(1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      setCurrentStep(0);
    }
  };

  const handleSkip = async () => {
    try {
      setIsLoading(true);
      await createWorkspace();
      showCustomToast("Workspace created successfully!", "check");
      setIsOpen(false);
      onWorkspaceCreated?.();
    } catch (error) {
      // Error already handled in createWorkspace
    }
  };

  const handleInviteAndFinish = async () => {
    if (!validateEmails(emails)) {
      return;
    }

    try {
      setIsLoading(true);

      // Create workspace first
      const workspace = await createWorkspace();

      // If there are emails, invite users
      if (emails.trim()) {
        const emailList = emails
          .split(/[\s,]+/)
          .filter((email) => email.trim())
          .map((email) => email.trim().toLowerCase());

        const result = await inviteUsers(workspace._id, emailList);

        if (
          (result.invitationSent && result.invitationSent.length > 0) ||
          (result.signupLinkSent && result.signupLinkSent.length > 0)
        ) {
          showCustomToast(
            "Workspace created and invitations sent successfully",
            "check"
          );
        }

        if (result.alreadyMember && result.alreadyMember.length > 0) {
          // showCustomToast(
          //   `User(s) already members: ${result.alreadyMember.join(", ")}`
          // );
          showCustomToast(`User(s) already members`, "exclamation");
        }

        if (result.alreadyInvited && result.alreadyInvited.length > 0) {
          showCustomToast(`User(s) already invited`, "exclamation");
        }
      }

      toast.success("Workspace created successfully!");
      setIsOpen(false);
      onWorkspaceCreated?.();
    } catch (error) {
      // Errors already handled in individual functions
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="onest-md bg-gray-50/[0.72] backdrop-blur-md z-[100000] fixed w-screen h-screen flex items-center justify-center"
    >
      <div
        className="p-6 flex flex-col justify-between rounded-[24px] w-[480px] h-[480px] bg-gray-200"
        ref={modalRef}
      >
        <div className="flex-1 overflow-hidden">
          <AnimatePresence mode="wait">
            {currentStep === 0 ? (
              <div>
                <p className="text-[20px] leading-[28px] font-medium text-white">
                  Create a workspace
                </p>
                <p className="mt-2 text-[14px] leading-[20px] text-white/[0.32] font-medium">
                  Bring your team together to work efficiently with the help of
                  workspaces
                </p>

                <div className="flex gap-[24px] items-center mt-8">
                  <div className={`${selectedColor.bg} text-[32px] font-semibold leading-[48px] ${selectedColor.text} uppercase shrink-0 rounded-[20px] w-[72px] h-[72px] flex items-center justify-center`}>
                    {workspaceName[0]?.toLowerCase() || "X"}
                  </div>
                  <div className="w-full">
                    <p className="text-[14px] leading-[20px] text-white/[0.32] font-medium">
                      Workspace name
                    </p>
                    <input
                      type="text"
                      placeholder="Enter a workspace name"
                      className={`rounded-[12px] w-full text-[14px] leading-[20px] px-[15px] outline-none ring-0 placeholder:text-white/[0.32] mt-2 text-white bg-white/[0.04] border-[1px] ${
                        nameError ? "border-red-700" : "border-white/[0.08]"
                      } h-[36px] py-[8px]`}
                      value={workspaceName}
                      onChange={(e) => {
                        setWorkspaceName(e.target.value);
                        setNameError(""); // Clear error when user starts typing
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === "Enter" && workspaceName.length) {
                          await handleNext();
                        }
                      }}
                      disabled={isLoading || isCheckingName}
                    />
                    {nameError && (
                      <p className="text-red-700 text-[12px] leading-[16px] mt-2">
                        {nameError}
                      </p>
                    )}
                  </div>
                </div>

                {/* <div className="flex gap-3 mt-8">
                  <Checkbox
                    onChange={() => setSelected((prev) => !prev)}
                    isSelected={selected}
                    // disabled={isLoading}
                  >
                    <p className="text-white/[0.32]">
                      Allow others with a @kodek.net email to join this
                      workspace
                    </p>
                  </Checkbox>
                </div> */}
              </div>
            ) : (
              <div>
                <p className="text-[20px] leading-[28px] font-medium text-white">
                  Invite members in your team!
                </p>
                <p className="mt-2 text-[14px] leading-[20px] text-white/[0.32] font-medium">
                  Bring your team together to work efficiently with help of
                  workspaces
                </p>

                <div className="mt-8">
                  <p className="text-[14px] leading-[20px] text-white/[0.32] font-medium">
                    Invite by email
                  </p>
                  <textarea
                    ref={textareaRef}
                    placeholder="Separate emails with space"
                    className={`rounded-[12px] no-scrollbar font-medium w-full text-[14px] leading-[20px] px-4 outline-none ring-0 placeholder:text-white/[0.32] mt-2 text-white bg-white/[0.04] border-[1px] ${
                      emailError ? "border-red-700" : "border-white/[0.08]"
                    } py-[12px] resize-none min-h-[84px] max-h-[200px] overflow-y-auto`}
                    style={{ height: "84px" }}
                    value={emails}
                    onChange={(e) => {
                      setEmails(e.target.value);
                      setEmailError(""); // Clear error when user starts typing
                      handleTextareaResize();
                    }}
                    disabled={isLoading}
                  />
                  {emailError && (
                    <p className="text-red-700 text-[12px] leading-[16px] mt-2">
                      {emailError}
                    </p>
                  )}
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Bottom section with buttons and progress */}
        <div>
          {currentStep === 0 ? (
            <button
              disabled={!workspaceName.length || isLoading || isCheckingName}
              onClick={handleNext}
              className="h-[44px] disabled:text-white/[0.32] disabled:bg-white/[0.04] text-[14px] leading-[20px] font-medium w-full bg-white hover:bg-white/[0.8] text-gray-50 rounded-[14px] flex items-center justify-center transition-all"
            >
              {isCheckingName
                ? "Checking..."
                : isLoading
                ? "Creating..."
                : "Next"}
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={handleBack}
                className="h-[44px] transition-all text-[14px] leading-[20px] font-medium flex-1 bg-white/[0.04] hover:bg-white/[0.08] text-white rounded-[14px] flex items-center justify-center"
                disabled={isLoading}
              >
                Back
              </button>
              {!emails.length ? (
                <button
                  onClick={handleSkip}
                  className="h-[44px] transition-all text-[14px] leading-[20px] font-medium flex-1 bg-white/[0.04] hover:bg-white/[0.08] text-white rounded-[14px] flex items-center justify-center"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating..." : "Skip"}
                </button>
              ) : (
                <button
                  onClick={handleInviteAndFinish}
                  className="h-[44px] transition-all text-[14px] leading-[20px] font-medium flex-1 bg-white hover:bg-white/[0.8] text-black rounded-[14px] flex items-center justify-center"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating..." : "Invite & finish"}
                </button>
              )}
            </div>
          )}

          {/* Progress indicator */}
          <div className="flex gap-1 justify-center mt-5">
            <motion.div
              className={`h-[8px] rounded-full transition-all ${
                currentStep === 0
                  ? "w-[24px] bg-white"
                  : "w-[8px] bg-white/[0.32]"
              }`}
              layout
            />
            <motion.div
              className={`h-[8px] rounded-full transition-all ${
                currentStep === 1
                  ? "w-[24px] bg-white"
                  : "w-[8px] bg-white/[0.32]"
              }`}
              layout
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CreateWorkspaceModal;
